import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface ModuleSummary {
  id: number;
  code: string;
  name: string;
  allowUnpaidAccess: boolean;
  assessmentResponseId: number;
  subModules: SubModuleSummary[];
  updatedAt: string | null;
  warnings: { count: number; description: string }[];
  questions: Record<string, string>;
}

export interface SubModuleSummary {
  id: number;
  code: string;
  displayOrder: number;
  assessmentModuleId: number;
  assessmentItems: ItemSummary[];
}

export interface ItemSummary {
  id: number;
  code: string;
  requirement: string;
  warning: string | null;
  assessmentItemResponses: ItemResponse[];
  standards: Record<string, number>;
  followUpItems: FollowUp[];
}

export interface ItemResponse {
  id?: number;
  meetsRequirement: boolean | null;
  notApplicable: boolean | null;
  notes: string | null;
  assessmentResponseId: number;
  assessmentItemId: number;
}

export interface FollowUp {
  id: number;
  note: string;
  completedAt: string | null;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'VERIFIED';
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  dueDate: string | null;
  assigneeId: number | null;
  assessmentItemCode?: string;
  assessmentSubModuleCode?: string;
}

export interface AssessmentResponseSummary {
  assessmentResponseId: number;
  assessmentId: number;
  createdAt: string;
  updatedAt: string | null;
  // The instant the catalog was read at, and the catalog release current then.
  // Absent on snapshot payloads captured before the stamp existed.
  asOf?: string;
  catalogReleaseVersion?: number | null;
  modules: ModuleResponseSummary[];
}

export interface ModuleResponseSummary {
  id: number;
  code: string;
  name: string;
  allowUnpaidAccess: boolean;
  displayOrder: number;
  updatedAt: string | null;
  summary: {
    profileQuestions: { total: number; answered: number };
    itemResponses: {
      total: number;
      warnings: { count: number; description: string }[];
      meetsStandardCount: number;
    };
    followUpItems: FollowUp[];
  };
}

export function useModuleSummary(moduleId: number | undefined, companyId: number | undefined) {
  return useQuery({
    queryKey: ['module-summary', moduleId, companyId],
    queryFn: () =>
      api.get<ModuleSummary>(`/api/AssessmentModules/${moduleId}/summary`),
    enabled: !!moduleId && !!companyId,
  });
}

export function useResponseSummary(companyId: number | undefined) {
  return useQuery({
    queryKey: ['response-summary', companyId],
    queryFn: () =>
      api.get<AssessmentResponseSummary>('/api/Companies/assessmentResponseSummary'),
    enabled: !!companyId,
  });
}

export function useUpsertItemResponse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      assessmentResponseId: number;
      assessmentItemId: number;
      meetsRequirement?: boolean | null;
      notApplicable?: boolean | null;
      notes?: string;
    }) => api.patch<ItemResponse>('/api/AssessmentItemResponses', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['module-summary'] });
      qc.invalidateQueries({ queryKey: ['response-summary'] });
    },
  });
}

export function useUpsertFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id?: number;
      clientId: number;
      assessmentItemId: number;
      assessmentResponseId: number;
      note: string;
      severity?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
      dueDate?: number;
      assigneeId?: number;
    }) => api.patch<FollowUp>('/api/FollowUps', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['module-summary'] });
    },
  });
}

export function useDeleteFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/FollowUps/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['module-summary'] }),
  });
}

export function useResolveFollowUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      api.post<FollowUp>(`/api/FollowUps/${id}/resolve`, {
        ...(note ? { resolutionNote: note } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['module-summary'] }),
  });
}

export interface SubAgencyAggregate {
  code: string;
  name: string;
  total: number;
  meeting: number;
  notMeeting: number;
}

export interface AgencyAggregate extends SubAgencyAggregate {
  subAgencies: SubAgencyAggregate[];
}

export interface StandardReportSummary {
  total: number;
  meeting: number;
  notMeeting: number;
  agencies: AgencyAggregate[];
}

interface StandardReportResponse {
  meta: { totalItems: number; totalPages: number; page: number; perPage: number };
  summary: StandardReportSummary;
}

export function useStandardReport(companyId: number | undefined) {
  return useQuery({
    queryKey: ['standard-report', companyId],
    queryFn: () =>
      api.get<StandardReportResponse>(
        '/api/Companies/assessmentStandardReport?perPage=1',
      ),
    enabled: !!companyId,
    select: (r) => r.summary,
  });
}

export interface StandardReportEntry {
  agencyCode: string;
  agencyName: string;
  subAgencyCode: string;
  subAgencyName: string;
  code: string;
  source: string;
  meeting: boolean | null;
  notApplicable: boolean | null;
}

interface AgencyStandardReportResponse extends StandardReportResponse {
  standards: StandardReportEntry[];
}

// Server caps perPage internally; pulling everything for one agency keeps the
// drill-down a single round-trip. Unpaginated for now — agencies max ~hundreds
// of standards and the table virtualizes naturally with browser scroll.
const DRILLDOWN_PER_PAGE = 1000;

export function useAgencyStandardReport(
  companyId: number | undefined,
  agencyCode: string | undefined,
) {
  return useQuery({
    queryKey: ['standard-report', companyId, 'agency', agencyCode],
    queryFn: () => {
      const filters = encodeURIComponent(
        JSON.stringify({ agencyCodes: [agencyCode] }),
      );
      return api.get<AgencyStandardReportResponse>(
        `/api/Companies/assessmentStandardReport?filters=${filters}&perPage=${DRILLDOWN_PER_PAGE}`,
      );
    },
    enabled: !!companyId && !!agencyCode,
  });
}
